import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import Colours from '@shared/ui/atoms/Colours';
import { selectActiveOffices } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { selectCurrentUser } from '@shared/store/auth.duck';
import {
  approveVisitorCheckinPendingRequest,
  generateVisitorCheckinCode,
  listVisitorCheckinCodes,
  listVisitorCheckinPendingRequests,
  rejectVisitorCheckinPendingRequest,
  revokeVisitorCheckinCode,
  VisitorCheckinCodeView,
  VisitorCheckinPendingRequestView,
} from '../../../services/VisitorCheckinAdminService';
import { HttpError } from '@shared/models/error.models';
import { isPremiumTierUser } from '@shared/utils/AccessControl';
import { ConfigurationSection } from '../../StyleComponents';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

const PENDING_POLL_INTERVAL_MS = 5000;

/**
 * Pairing administration for office signage screens. Shares the pairing-code
 * infrastructure with the visitor check-in kiosk, but only shows SIGNAGE
 * codes and requests; the kiosk panel shows the KIOSK ones.
 */
export function SignageDevicesSection() {
  const user = useSelector(selectCurrentUser);
  const signageEnabled = !!user?.companyEntity?.signageEnabled;
  if (!signageEnabled || !isPremiumTierUser(user)) return null;
  return <SignageDevicesSectionBody />;
}

function SignageDevicesSectionBody() {
  const offices = useSelector(selectActiveOffices);
  // Signage codes bind to a whole office; the screen picks floors on-device.
  const rootOffices = offices.filter((o: any) => !o.parentId);

  const [codes, setCodes] = useState<VisitorCheckinCodeView[] | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedOfficeId, setSelectedOfficeId] = useState<number | ''>('');
  const [label, setLabel] = useState('');
  const [urlCopied, setUrlCopied] = useState(false);

  const signageUrl = `${window.location.origin}/signage`;

  const copySignageUrl = async () => {
    try {
      await navigator.clipboard.writeText(signageUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch {
      /* clipboard not available */
    }
  };

  useEffect(() => {
    listVisitorCheckinCodes().then(d => {
      setCodes(d.codes.filter(c => c.deviceType === 'SIGNAGE'));
    });
  }, []);

  useEffect(() => {
    if (selectedOfficeId === '' && rootOffices.length > 0) {
      setSelectedOfficeId(rootOffices[0].id);
    }
  }, [rootOffices, selectedOfficeId]);

  const handleGenerate = async () => {
    if (selectedOfficeId === '') { setError('Pick an office'); return; }
    setError(null);
    setBusy(true);
    try {
      const created = await generateVisitorCheckinCode(Number(selectedOfficeId), label.trim() || null, 'SIGNAGE');
      setCodes(prev => prev ? [created, ...prev] : [created]);
      setLabel('');
    } catch (err: any) {
      setError(err?.message || 'Could not generate code');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (id: number) => {
    if (!window.confirm('Revoke this code? Any screens paired with it will go back to the pairing screen.')) return;
    setBusy(true);
    try {
      await revokeVisitorCheckinCode(id);
      setCodes(prev => prev ? prev.map(c => c.id === id ? { ...c, revoked: true, activeDeviceCount: 0 } : c) : prev);
    } finally { setBusy(false); }
  };

  if (!codes) return <SimpleSpinner size={32} />;

  return (
    <ConfigurationSection>
      <BodyLarge>Screens and pairing</BodyLarge>

      <SignagePendingRequests />

      <InfoBox>
        <BodyLarge weight={600} style={{ marginBottom: 8 }}>How to set up a screen</BodyLarge>
        <InstructionList>
          <li>
            <BodySmall>On the screen, open this URL in a fullscreen browser:</BodySmall>
            <UrlRow>
              <UrlValue>{signageUrl}</UrlValue>
              <OutlineButton
                text={urlCopied ? 'Copied' : 'Copy link'}
                size={ButtonSize.Small}
                click={copySignageUrl}
              />
            </UrlRow>
          </li>
          <li><BodySmall>Generate a Waypoint code below for the office the screen is in.</BodySmall></li>
          <li><BodySmall>Enter the 8-digit code on the screen, then approve the request that appears here.</BodySmall></li>
          <li><BodySmall>The screen shows the floor plan of who&apos;s in today and lets you switch between floors.</BodySmall></li>
        </InstructionList>
      </InfoBox>

      <SubBlock>
        <BodyRegular>Generate a new Waypoint code</BodyRegular>
        <InlineRow>
          <Select
            value={selectedOfficeId}
            onChange={e => setSelectedOfficeId(e.target.value === '' ? '' : Number(e.target.value))}
            disabled={busy}
          >
            {rootOffices.length === 0 && <option value="">No offices available</option>}
            {rootOffices.map((o: any) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </Select>
          <Input
            type="text"
            maxLength={80}
            placeholder="Label (optional, e.g. Reception TV)"
            value={label}
            onChange={e => setLabel(e.target.value)}
            disabled={busy}
          />
          <PrimaryButton text={'Generate code'} size={ButtonSize.Small} click={handleGenerate} disabled={busy} />
        </InlineRow>
        {error && <ErrorBox>{error}</ErrorBox>}
      </SubBlock>

      <SubBlock>
        <BodyRegular>Waypoint codes</BodyRegular>
        {codes.length === 0 ? (
          <BodySmall style={{ marginTop: 8, color: Colours.darkGrey }}>No Waypoint codes generated yet.</BodySmall>
        ) : (
          codes.map(code => {
            const expiry = new Date(code.expiresAt);
            const expired = expiry.getTime() < Date.now();
            return (
              <CodeRowWrap key={code.id}>
                <CodeRowHeader>
                  <CodeValue>{code.code}</CodeValue>
                  {code.label && <CodeLabel>{code.label}</CodeLabel>}
                  {code.revoked && <Pill $variant="danger">Revoked</Pill>}
                  {!code.revoked && expired && <Pill $variant="muted">Expired</Pill>}
                  {!code.revoked && !expired && <Pill $variant="ok">Active</Pill>}
                </CodeRowHeader>
                <BodySmall style={{ color: Colours.darkGrey }}>
                  Office: {code.officeLabel || `#${code.officeId}`} · Expires {expiry.toLocaleString()} · {code.activeDeviceCount} active screen{code.activeDeviceCount === 1 ? '' : 's'}
                </BodySmall>
                {!code.revoked && (
                  <CodeRowActions>
                    <OutlineButton text={'Revoke'} size={ButtonSize.Small} click={() => handleRevoke(code.id)} disabled={busy} />
                  </CodeRowActions>
                )}
              </CodeRowWrap>
            );
          })
        )}
      </SubBlock>
    </ConfigurationSection>
  );
}

function SignagePendingRequests() {
  const [pending, setPending] = useState<VisitorCheckinPendingRequestView[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);

  const refresh = async () => {
    try {
      const all = await listVisitorCheckinPendingRequests();
      setPending(all.filter(p => p.deviceType === 'SIGNAGE'));
    } catch {
      /* swallow polling errors */
    }
  };

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => { void refresh(); }, PENDING_POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, []);

  const decide = async (requestId: string, action: 'approve' | 'reject') => {
    setBusy(requestId);
    setConflictMessage(null);
    try {
      if (action === 'approve') await approveVisitorCheckinPendingRequest(requestId);
      else await rejectVisitorCheckinPendingRequest(requestId);
      setPending(prev => prev.filter(p => p.requestId !== requestId));
    } catch (err: any) {
      if (err instanceof HttpError && err.statusCode === 409) {
        setConflictMessage('Already handled — refreshing list');
        await refresh();
      } else {
        setConflictMessage(err?.message || `Could not ${action}`);
      }
    } finally {
      setBusy(null);
    }
  };

  if (pending.length === 0) return null;

  return (
    <PendingPanel>
      <BodyLarge weight={600} style={{ marginBottom: 8 }}>
        Pending Waypoint pairing requests
      </BodyLarge>
      <BodyRegular style={{ marginBottom: 12, color: Colours.darkGrey }}>
        A screen has entered a Waypoint code and is waiting for your approval. Requests expire after 5 minutes.
      </BodyRegular>
      {conflictMessage && <ErrorBox>{conflictMessage}</ErrorBox>}
      {pending.map(p => (
        <PendingRowWrap key={p.requestId}>
          <CodeRowHeader>
            <CodeValue>{p.code}</CodeValue>
            {p.label && <CodeLabel>{p.label}</CodeLabel>}
            <Pill $variant="ok">Waypoint</Pill>
          </CodeRowHeader>
          <BodySmall style={{ color: Colours.darkGrey }}>
            Office: {p.officeLabel || `#${p.officeId}`}
            {p.clientIp ? ` · From ${p.clientIp}` : ''}
          </BodySmall>
          <CodeRowActions>
            <PrimaryButton text={'Approve'} size={ButtonSize.Small} click={() => decide(p.requestId, 'approve')} disabled={busy !== null} />
            <OutlineButton text={'Reject'} size={ButtonSize.Small} click={() => decide(p.requestId, 'reject')} disabled={busy !== null} />
          </CodeRowActions>
        </PendingRowWrap>
      ))}
    </PendingPanel>
  );
}

const SubBlock = styled.div`
  margin-top: 20px;
`;

const PendingPanel = styled.div`
  margin-top: 16px;
  padding: 16px 20px;
  background: ${Colours.orange10};
  border: 1px solid ${Colours.orange20};
  border-radius: 8px;
`;

const PendingRowWrap = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: white;
  border: 1px solid ${Colours.lightGrey};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InfoBox = styled.div`
  margin-top: 16px;
  padding: 16px 20px;
  background: ${Colours.blue05};
  border: 1px solid ${Colours.blue20};
  border-radius: 8px;
`;

const InstructionList = styled.ol`
  margin: 8px 0;
  padding-left: 20px;
  li {
    margin-bottom: 4px;
  }
  li > p {
    margin: 0;
  }
`;

const UrlRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 8px 0 4px 0;
  flex-wrap: wrap;
`;

const UrlValue = styled.div`
  padding: 8px 12px;
  background: white;
  border: 1px solid ${Colours.mildGrey};
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 14px;
  word-break: break-all;
  flex: 1;
  min-width: 240px;
`;

const InlineRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const Input = styled.input`
  height: 32px;
  padding: 0 10px;
  border: 1px solid ${Colours.mildGrey};
  border-radius: 4px;
  font-size: 14px;
  min-width: 200px;
`;

const Select = styled.select`
  height: 32px;
  padding: 0 10px;
  border: 1px solid ${Colours.mildGrey};
  border-radius: 4px;
  font-size: 14px;
  min-width: 220px;
  background: white;
`;

const ErrorBox = styled.div`
  margin-top: 12px;
  padding: 8px 12px;
  border: 1px solid ${Colours.red};
  color: ${Colours.red};
  border-radius: 4px;
  font-size: 13px;
`;

const CodeRowWrap = styled.div`
  margin-top: 12px;
  padding: 12px;
  border: 1px solid ${Colours.lightGrey};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CodeRowHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const CodeRowActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const CodeValue = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 2px;
`;

const CodeLabel = styled.span`
  font-family: proxima-nova;
  font-size: 14px;
  color: ${Colours.darkGrey};
`;

const Pill = styled.span<{ $variant: 'ok' | 'muted' | 'danger' }>`
  font-family: proxima-nova;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${p => p.$variant === 'ok' ? Colours.green : (p.$variant === 'danger' ? Colours.red : Colours.iconGrey)};
  color: ${p => p.$variant === 'muted' ? Colours.darkGrey : 'white'};
`;
